/**
 * Automatic-reload reporter (RUM).
 *
 * Every code path that reloads the tab on its own stamps its reason into
 * ``sessionStorage`` first; the load that follows picks the stamp up and posts
 * it once. A reload cannot report itself — the in-flight request dies with the
 * document — so the stamp has to outlive it.
 *
 * Why this exists (KAN-196): users saw the page reload outside deploy windows
 * and we had no way to tell which of the self-healing paths fired. The stamp is
 * written by the bootstrap script in ``index.html`` too, which runs before any
 * app code, so even a reload that happens pre-mount is attributed.
 *
 * Privacy: reason slug, parameterised path (UUIDs replaced, query string
 * dropped), build ids and a truncated error message — never page content.
 */

import { clientBuildId } from '@/lib/build-version';
import { sanitizePath } from '@/lib/telemetry/slow-requests';

/**
 * Kept in sync with the key the ``index.html`` bootstrap script writes — that
 * script cannot import from here.
 */
const STAMP_KEY = '9brains.reload-reason';
const REPORT_PATH = '/api/v1/client-metrics/reloads';
const MAX_DETAIL_LENGTH = 300;

/** Why the app reloaded itself. Kebab-case; mirrored in the backend log line. */
export type PageReloadReason =
  /** A newer deploy is live and the tab reached a safe boundary. */
  | 'deploy-version'
  /** A route's chunk failed to load AND /version.json confirmed a newer build. */
  | 'route-chunk-error'
  /** An entry asset failed before the app booted (bootstrap script in index.html). */
  | 'bootstrap-asset-error'
  /** A chunk kept failing on the current build, so its cached copies were rewritten. */
  | 'asset-cache-refresh'
  /** Restored from the back/forward cache under a *different* user. */
  | 'bfcache-user-changed'
  /** The stored workspace became inaccessible (403) and had to be re-picked. */
  | 'tenant-forbidden'
  /** Workspace switch failed on a stale chunk. */
  | 'tenant-switch-chunk-error';

interface ReloadStamp {
  reason: PageReloadReason;
  path: string;
  at: number;
  detail?: string;
  serverBuildId?: string;
  tabAgeMs?: number;
}

/**
 * Record why we are about to reload. Call immediately BEFORE triggering the
 * reload; the next load reports it. Never throws — a failed stamp must not
 * block the reload it describes.
 */
export function stampReloadReason(
  reason: PageReloadReason,
  extra?: { detail?: string; serverBuildId?: string }
): void {
  if (typeof window === 'undefined') return;
  try {
    const stamp: ReloadStamp = {
      reason,
      // Pass the pathname, not the href: relative input always resolves
      // against our own origin, so the same-origin check can never reject it.
      path: sanitizePath(window.location.pathname) ?? '/',
      at: Date.now(),
      // performance.now() is time-since-this-document-started, i.e. how long
      // the tab had been open — the fastest way to tell "reloaded while
      // working" from "reloaded right after opening".
      tabAgeMs: Math.round(performance.now()),
    };
    if (extra?.detail) stamp.detail = extra.detail.slice(0, MAX_DETAIL_LENGTH);
    if (extra?.serverBuildId) stamp.serverBuildId = extra.serverBuildId;
    sessionStorage.setItem(STAMP_KEY, JSON.stringify(stamp));
  } catch {
    // sessionStorage unavailable (private mode) — losing the attribution is
    // fine, breaking the reload is not.
  }
}

function readAndClearStamp(): ReloadStamp | null {
  try {
    const raw = sessionStorage.getItem(STAMP_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STAMP_KEY);
    const parsed: unknown = JSON.parse(raw);
    const stamp = parsed as Partial<ReloadStamp> | null;
    if (!stamp || typeof stamp.reason !== 'string' || typeof stamp.path !== 'string') return null;
    return stamp as ReloadStamp;
  } catch {
    return null;
  }
}

/**
 * Report the reload that just happened, if any. Idempotent by construction:
 * the stamp is cleared as it is read, so a re-mount cannot double-report.
 */
export function reportPendingReload(): void {
  if (typeof window === 'undefined') return;
  const stamp = readAndClearStamp();
  if (!stamp) return;

  const payload = {
    reason: stamp.reason,
    path: stamp.path,
    clientBuildId: clientBuildId() ?? null,
    serverBuildId: stamp.serverBuildId ?? null,
    detail: stamp.detail ?? null,
    tabAgeMs: stamp.tabAgeMs ?? null,
  };

  // Deliberately NOT fetchWithAuth: a background reporter must never trigger
  // the 401 login redirect. Cookies carry the auth; errors are swallowed.
  void fetch(REPORT_PATH, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Fire-and-forget.
  });

  if (process.env.NODE_ENV !== 'production') {
    console.info('[PageReloadReporter]', payload);
  }
}
